import { useMemo } from "react";
import Box from "@mui/material/Box";
import {
  ReferenceItemsCascadePicker,
  type ReferenceCascadeSelection,
  type ReferenceDimensionSelection,
} from "../TaggingPlan/ClassificationCascadePicker";

export type EnhancedTaggingRuleObjectSelection = {
  objectType: string;
  objectSubType: string;
  /** Display labels for selected values (comma-joined) or "All". */
  objectSubTypeName?: string;
  /** Selected Reference Dimension concept code (e.g. DocType, TherapeuticArea). */
  referenceConceptCode?: string;
  /** Display label for `referenceConceptCode`. */
  referenceConceptDisplayName?: string;
  /**
   * All selected Reference Dimensions. Submit maps this list to
   * `reference_items`, same as Classification.
   */
  referenceDimensions?: ReferenceDimensionSelection[];
};

/** Legacy list/edit rows only store subtype — treat as Doc Type when present. */
function resolveReferenceConceptForHydrate(
  value: EnhancedTaggingRuleObjectSelection,
): Pick<
  ReferenceCascadeSelection,
  "referenceConceptCode" | "referenceConceptDisplayName"
> {
  const hasValues = Boolean(value.objectSubType.trim());
  const conceptCode = value.referenceConceptCode?.trim();
  if (conceptCode) {
    return {
      referenceConceptCode: conceptCode,
      referenceConceptDisplayName:
        value.referenceConceptDisplayName?.trim() || conceptCode,
    };
  }
  if (!hasValues) {
    return {
      referenceConceptCode: undefined,
      referenceConceptDisplayName: undefined,
    };
  }
  return {
    referenceConceptCode: "DocType",
    referenceConceptDisplayName:
      value.referenceConceptDisplayName?.trim() || "Doc Type",
  };
}

type EnhancedTaggingRuleObjectStepProps = Readonly<{
  value: EnhancedTaggingRuleObjectSelection;
  onChange: (next: EnhancedTaggingRuleObjectSelection) => void;
  showValidation?: boolean;
  disabled?: boolean;
  /** Closed-field join between object and dimension. Default keeps the middle dot. */
  labelSeparator?: string;
}>;

export default function EnhancedTaggingRuleObjectStep({
  value,
  onChange,
  showValidation = false,
  disabled = false,
  labelSeparator,
}: EnhancedTaggingRuleObjectStepProps) {
  // Concept V3: Object alone is enough; Dimension/Value optional.
  // Do not prefetch /plans/types here — ReferenceItemsCascadePicker loads on open.
  const requiresSubType = false;

  const cascadeValue = useMemo<ReferenceCascadeSelection>(() => {
    const concept = resolveReferenceConceptForHydrate(value);
    return {
      objectType: value.objectType,
      objectSubType: value.objectSubType,
      objectSubTypeName: value.objectSubTypeName,
      ...concept,
      referenceDimensions: value.referenceDimensions,
      conceptCode: "",
      conceptValues: [],
      allValues: false,
    };
  }, [value]);

  const showError =
    showValidation && !isObjectSelectionComplete(value, requiresSubType);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <ReferenceItemsCascadePicker
        value={cascadeValue}
        onChange={(next) => {
          onChange({
            objectType: next.objectType,
            objectSubType: next.objectSubType,
            objectSubTypeName: next.objectSubTypeName,
            referenceConceptCode: next.referenceConceptCode,
            referenceConceptDisplayName: next.referenceConceptDisplayName,
            referenceDimensions: next.referenceDimensions,
          });
        }}
        error={showError}
        helperText={showError ? "Please select Reference Items." : undefined}
        disabled={disabled}
        labelSeparator={labelSeparator}
      />
    </Box>
  );
}

export function createEmptyObjectSelection(): EnhancedTaggingRuleObjectSelection {
  return { objectType: "", objectSubType: "" };
}

/**
 * `requiresSubType` stays for Triage. Conditional Classification passes false
 * so selecting only the object (for example Insight) is complete.
 */
export function isObjectSelectionComplete(
  selection: EnhancedTaggingRuleObjectSelection,
  requiresSubType = false,
): boolean {
  if (!selection.objectType.trim()) return false;
  if (requiresSubType && !selection.objectSubType.trim()) return false;
  return true;
}
